<template>
  <div>
    <!-- Заголовок -->
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900">{{ pageTitle }}</h1>
    </div>

    <!-- Main Content Card -->
    <div class="bg-white shadow-sm rounded-xl p-6 border border-gray-200">
      <div v-if="hasOrganizationAccess && !isClinicManagerMode" class="mb-5 flex flex-col lg:flex-row lg:justify-between lg:items-center gap-3">
        <div class="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50">
          <button
            @click="appointmentsScope = 'mine'"
            class="px-4 py-2 text-sm font-medium rounded-md transition-colors"
            :class="appointmentsScope === 'mine' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'"
          >
            {{ $t('appointment.myAppointments') }}
          </button>
          <button
            @click="appointmentsScope = 'organization'"
            class="px-4 py-2 text-sm font-medium rounded-md transition-colors"
            :class="appointmentsScope === 'organization' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'"
          >
            {{ $t('appointment.organizationAppointments') }}
          </button>
        </div>

        <div v-if="appointmentsScope === 'organization' && organizationMemberships.length > 1" class="flex items-center gap-2">
          <label class="text-sm text-gray-600">{{ $t('organizationPatients.organization') || 'Организация' }}:</label>
          <select
            v-model="selectedOrganizationId"
            class="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">{{ $t('common.all') || 'Все' }}</option>
            <option v-for="membership in organizationMemberships" :key="membership.organization" :value="membership.organization">
              {{ membership.organization_name }}
            </option>
          </select>
        </div>
      </div>

      <!-- Header with Title, Date Filter and Tab Navigation -->
      <div class="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-6 space-y-4 lg:space-y-0">
        <div class="flex flex-col sm:flex-row sm:items-center sm:space-x-6 space-y-4 sm:space-y-0">
          
          <!-- Date Range Filter -->
          <div class="flex items-center space-x-2 rounded-xl p-3 shadow-sm bg-gray-50">
            <div class="flex items-center justify-center w-8 h-8 rounded-lg">
              <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
            </div>
            
            <div class="flex items-center space-x-2">
              <div class="relative">
                <input 
                  v-model="dateRange.startDate"
                  type="date" 
                  class="modern-date-input text-sm bg-white border border-blue-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  :placeholder="$t('common.startDate')"
                  @change="onDateRangeChange"
                />
              </div>
              
              <div class="flex items-center justify-center w-6 h-6 bg-blue-100 rounded-full">
                <svg class="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
                </svg>
              </div>
              
              <div class="relative">
                <input 
                  v-model="dateRange.endDate"
                  type="date" 
                  class="modern-date-input text-sm bg-white border border-blue-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  :placeholder="$t('common.endDate')"
                  @change="onDateRangeChange"
                />
              </div>
            </div>
            
            <button 
              @click="resetDateRange"
              class="flex items-center justify-center w-8 h-8 bg-red-100 hover:bg-red-200 rounded-lg transition-colors duration-200 group"
              :title="$t('common.resetFilter')"
            >
              <svg class="w-4 h-4 text-red-600 group-hover:text-red-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <div v-if="isClinicManagerMode" class="flex items-center space-x-2">
          <label class="text-sm text-gray-600">Врач:</label>
          <select
            v-model="selectedManagerDoctor"
            class="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 min-w-56"
          >
            <option value="">Все врачи</option>
            <option
              v-for="doctor in managerDoctors"
              :key="doctor.id"
              :value="String(doctor.id)"
            >
              {{ getDoctorDisplayName(doctor) }}
            </option>
          </select>
        </div>
        
        <!-- Tab Navigation -->
        <div class="flex space-x-2">
          <button 
            v-for="tab in tabs" 
            :key="tab.key"
            @click="activeTab = tab.key"
            class="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
            :class="activeTab === tab.key ? 
              'bg-blue-100 text-blue-700' : 
              'text-gray-600 hover:bg-gray-100'"
          >
            {{ tab.label }}
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center p-6">
        <HeartbeatLoader :text="$t('common.loading')" />
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="p-4 bg-red-50 text-red-600 rounded-md">
        {{ error }}
      </div>

      <!-- Content -->
      <div v-else>
        <!-- Appointments Table -->
        <div v-if="currentAppointments.length > 0" class="overflow-x-auto">
          <table class="min-w-full bg-white">
            <thead>
              <tr class="bg-gray-50 text-gray-700">
                <th class="py-3 px-4 text-left font-medium w-2/5">{{ $t('patientDetails.appointmentType') }}</th>
                <th class="py-3 px-4 text-left font-medium w-1/5">{{ $t('patientDetails.dateTime') }}</th>
                <th class="py-3 px-4 text-left font-medium w-1/4">{{ $t('patientDetails.visitReason') }}</th>
                <th class="py-3 px-4 text-left font-medium w-1/12">{{ $t('common.actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="appointment in currentAppointments" 
                :key="appointment.id"
                class="border-b border-gray-200 hover:bg-gray-50 transition-colors"
              >
                <!-- Type Column -->
                <td class="py-3 px-4">
                  <div class="flex items-center">
                    <div class="w-8 h-8 rounded-full flex items-center justify-center mr-2"
                         :class="appointment.call_type === 'nurse_call' ? 'bg-green-100' : 'bg-blue-100'">
                      <svg v-if="appointment.call_type === 'nurse_call'" class="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.5 6c-2.61.7-5.67 1-8.5 1s-5.89-.3-8.5-1L3 8c0 .55.45 1 1 1h1v10c0 .55.45 1 1 1h2c.55 0 1-.45 1-1v-3h6v3c0 .55.45 1 1 1h2c.55 0 1-.45 1-1V9h1c.55 0 1-.45 1-1l-.5-2zM12 6c.55 0 1-.45 1-1s-.45-1-1-1-1 .45-1 1 .45 1 1 1z"/>
                      </svg>
                      <svg v-else class="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                      </svg>
                    </div>
                    <div>
                      <div class="font-medium">{{ getPatientName(appointment) }}</div>
                      <div class="text-xs text-gray-500">
                        {{ appointment.call_type === 'nurse_call' ? $t('nurse.videoCall') : $t('patientDetails.doctorAppointment') }}
                      </div>
                    </div>
                  </div>
                </td>

                <!-- Date/Time Column -->
                <td class="py-3 px-4">
                  <div class="text-sm">
                    <div class="font-medium">{{ formatDate(getAppointmentDate(appointment)) }}</div>
                    <div class="text-gray-500">
                      {{ formatTime(getAppointmentStartTime(appointment)) }}
                      <span v-if="getAppointmentEndTime(appointment)"> - {{ formatTime(getAppointmentEndTime(appointment)) }}</span>
                    </div>
                  </div>
                </td>

                <!-- Reason Column -->
                <td class="py-3 px-4">
                  <div>{{ getVisitReasonText(appointment) || $t('patientDetails.noReasonSpecified') }}</div>
                  <div v-if="appointment.call_type === 'nurse_call' && appointment.notes" class="text-xs text-gray-500 mt-1">
                    {{ appointment.notes }}
                  </div>
                  <div class="flex items-center space-x-2 mt-1">
                  <!-- Status badge -->
                    <div class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                       :class="getStatusClass(appointment.status)">
                    {{ getStatusText(appointment.status) }}
                    </div>
                    <div
                      v-if="showBookingStatusForAppointment(appointment)"
                      class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                      :class="getBookingStatusClass(appointment.booking_status)"
                    >
                      {{ getBookingStatusText(appointment.booking_status) }}
                    </div>
                    <!-- Consilium badge -->
                    <div v-if="appointment.is_consilium" class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      {{ $t('consilium.consilium') }}
                    </div>
                  </div>
                </td>

                <!-- Actions Column -->
                <td class="py-3 px-4">
                  <div class="flex space-x-1">
                    <button 
                      v-if="!isClinicManagerMode && !isOrganizationScope && appointment.status === 'scheduled'"
                      @click="startConsultation(appointment)"
                      class="px-3 py-1 text-xs bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    >
                      {{ $t('appointment.startAppointment') }}
                    </button>
                    
                    <button 
                      v-if="!isClinicManagerMode && !isOrganizationScope && (appointment.status === 'scheduled' || appointment.status === 'in_progress')"
                      @click="openConsiliumDialog(appointment)"
                      class="px-3 py-1 text-xs bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                      :title="$t('consilium.inviteTooltip')"
                    >
                      {{ $t('consilium.invite') }}
                    </button>
                    
                    <button 
                      v-if="!isClinicManagerMode && !isOrganizationScope && appointment.video_room_url && (appointment.status === 'scheduled' || appointment.status === 'in_progress')"
                      @click="joinVideoCall(appointment)"
                      class="px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      {{ $t('appointment.joinVideoCall') }}
                    </button>
                    
                    <button 
                      v-if="!isClinicManagerMode && !isOrganizationScope && canCompleteAppointment(appointment)"
                      @click="completeConsultation(appointment)"
                      class="px-3 py-1 text-xs bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
                    >
                      {{ $t('appointments.completeAppointment') }}
                    </button>
                    
                    <button 
                      v-if="appointment.status === 'completed' && hasAppointmentMedia(appointment)"
                      @click="showRoomRecords(appointment)"
                      class="w-8 h-8 flex items-center justify-center bg-white-600 text-white rounded-md hover:bg-white-700 transition-colors"
                      :title="$t('patientDetails.viewRecords')"
                    >
                      <svg class="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd"></path>
                      </svg>
                    </button>
                    
                    <button 
                      v-if="!isClinicManagerMode && !isOrganizationScope && appointment.status === 'completed'"
                      @click="showAppointmentConclusion(appointment)"
                      class="w-8 h-8 flex items-center justify-center bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 transition-colors"
                      :title="'Просмотр заключения'"
                    >
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                      </svg>
                    </button>
                    
                    <button 
                      v-if="!isClinicManagerMode && !isOrganizationScope && canLeaveConsilium(appointment)"
                      @click="confirmLeaveConsilium(appointment)"
                      class="px-3 py-1 text-xs bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
                      :title="$t('consilium.leaveConsiliumTooltip')"
                    >
                      {{ $t('consilium.leaveConsilium') }}
                    </button>

                    <button
                      v-if="canSetBookingStatus(appointment, 'confirmed')"
                      @click="setBookingStatus(appointment, 'confirmed')"
                      class="px-3 py-1 text-xs bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors"
                    >
                      Подтвердить
                    </button>

                    <button
                      v-if="canSetBookingStatus(appointment, 'rejected')"
                      @click="setBookingStatus(appointment, 'rejected')"
                      class="px-3 py-1 text-xs bg-rose-600 text-white rounded-md hover:bg-rose-700 transition-colors"
                    >
                      Отклонить
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty State -->
        <div v-else class="text-center p-8 text-gray-500">
          <svg class="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
          </svg>
          <p v-if="isOrganizationScope">{{ $t('appointment.noOrganizationAppointments') || 'Приемы пациентов организации не найдены' }}</p>
          <p v-else>{{ getEmptyAppointmentsMessage() }}</p>
        </div>
      </div>
  </div>
  </div>

  <!-- Room Records Dialog -->
  <div v-if="showRoomRecordsDialog" class="fixed inset-0 z-50 overflow-y-auto">
    <!-- Backdrop overlay -->
    <div class="fixed inset-0 bg-black opacity-50" @click="closeRoomRecordsDialog"></div>
    
    <!-- Modal dialog -->
    <div class="flex items-center justify-center min-h-screen p-4">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-auto z-10">
        <div class="px-6 py-4 border-b">
          <h2 class="text-xl font-semibold text-gray-800">{{ $t('patientDetails.records.title') }}</h2>
          <p v-if="selectedAppointmentForRecords" class="text-sm text-gray-600 mt-1">
            {{ $t('patientDetails.records.roomName') }}: {{ selectedAppointmentForRecords.video_room_name || 'Скрыто до разрешения пациента' }}
          </p>
          <div class="flex space-x-3 mt-3">
            <button 
              @click="showChatForRoom"
              class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center text-sm"
              :class="{
                'bg-blue-700': showChatPanel,
                'bg-gray-400 hover:bg-gray-400 cursor-not-allowed': mediaAccessNotice.visible
              }"
              :disabled="mediaAccessNotice.visible"
            >
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
              </svg>
              {{ showChatPanel ? $t('patientDetails.records.hideChat') : $t('patientDetails.records.showChat') }}
            </button>
          </div>
        </div>
        
        <div class="p-6">
          <!-- Chat Panel Section -->
          <div v-if="showChatPanel" class="mb-6">
            <div class="bg-gray-50 rounded-lg border">
              <!-- Chat Header -->
              <div class="px-4 py-3 border-b bg-blue-50 rounded-t-lg">
                <div class="flex justify-between items-center">
                  <div class="flex items-center">
                    <svg class="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
                    </svg>
                    <h4 class="font-medium text-gray-900">{{ $t('patientDetails.chat.meetingChatTitle') }}</h4>
                  </div>
                  <button 
                    @click="closeChatPanel"
                    class="text-gray-500 hover:text-gray-700"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                </div>
              </div>

              <!-- Chat Content -->
              <div class="p-4">
                <div v-if="roomChatLoading" class="flex justify-center p-4">
                  <HeartbeatLoader />
                  <span class="ml-2 text-sm text-gray-600">{{ $t('patientDetails.chat.loading') }}</span>
                </div>

                <div v-else-if="roomChatError" class="text-center p-4">
                  <div class="text-gray-500 text-sm">{{ roomChatError }}</div>
                </div>

                <div v-else-if="roomChatMessages.length > 0" class="space-y-3 max-h-64 overflow-y-auto">
                  <div 
                    v-for="message in roomChatMessages" 
                    :key="message.id"
                    class="flex flex-col"
                  >
                    <div class="flex items-start space-x-3">
                      <!-- Avatar -->
                      <div class="flex-shrink-0">
                        <div class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium"
                             :class="message.sender_name === 'Врач' || message.sender_name === 'Медсестра' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'">
                          {{ message.sender_name.charAt(0).toUpperCase() }}
                        </div>
                      </div>
                      
                      <!-- Message Content -->
                      <div class="flex-1 min-w-0">
                        <div class="flex items-center space-x-2">
                          <span class="font-medium text-sm text-gray-900">{{ message.sender_name }}</span>
                          <span class="text-xs text-gray-500">{{ formatChatTimestamp(message.created_at) }}</span>
                        </div>
                        
                        <!-- Parsed Message Content -->
                        <div class="mt-1 p-3 rounded-lg max-w-md"
                             :class="message.sender_name === 'Врач' || message.sender_name === 'Медсестра' ? 'bg-blue-50 text-gray-800' : 'bg-gray-100 text-gray-800'">
                          
                          <!-- Text Message -->
                          <div v-if="getMessageText(message)" class="mb-2">
                            <p class="text-sm">{{ getMessageText(message) }}</p>
                          </div>
                          
                          <!-- File Attachments -->
                          <div v-if="getMessageFiles(message).length > 0" class="space-y-2">
                            <div 
                              v-for="file in getMessageFiles(message)" 
                              :key="file.id"
                              class="bg-white rounded-lg p-2 border"
                            >
                              <!-- Image Files -->
                              <div v-if="file.file_type && file.file_type.startsWith('image/')" class="space-y-2">
                                <img 
                                  :src="file.public_url" 
                                  :alt="file.original_name"
                                  class="max-w-full h-auto rounded max-h-40 cursor-pointer"
                                  @click="openImageModal(file.public_url, file.original_name)"
                                />
                                <div class="text-xs text-gray-500">
                                  📷 {{ file.original_name }} ({{ formatFileSize(file.file_size) }})
                                </div>
                              </div>
                              
                              <!-- Other Files -->
                              <div v-else class="flex items-center space-x-2">
                                <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                </svg>
                                <div class="flex-1 min-w-0">
                                  <a 
                                    :href="file.public_url" 
                                    target="_blank"
                                    class="text-blue-600 hover:text-blue-800 text-sm font-medium truncate block"
                                  >
                                    {{ file.original_name }}
                                  </a>
                                  <div class="text-xs text-gray-500">{{ formatFileSize(file.file_size) }}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <!-- Empty message indicator -->
                          <div v-else class="text-sm text-gray-500 italic">
                            Пустое сообщение
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-else class="text-center p-6">
                  <div class="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
                    </svg>
                  </div>
                  <h4 class="text-sm font-medium text-gray-900 mb-1">Нет сообщений</h4>
                  <p class="text-sm text-gray-600">В этой встрече нет обмена сообщениями</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Video Player Section -->
          <div v-if="selectedVideoUrl && showVideoPlayer" class="mb-6">
            <div class="bg-black rounded-lg overflow-hidden">
              <video 
                :src="selectedVideoUrl" 
                controls 
                class="w-full h-64 md:h-96"
                controlsList="nodownload"
                @loadedmetadata="onVideoLoaded"
              >
                Ваш браузер не поддерживает воспроизведение видео.
              </video>
            </div>
            <div class="flex justify-between items-center mt-3">
              <div class="text-sm text-gray-600">
                <svg class="w-4 h-4 inline mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd"></path>
                </svg>
                Воспроизведение записи встречи
              </div>
              <button 
                @click="closeVideoPlayer"
                class="text-gray-500 hover:text-gray-700 text-sm"
              >
                Закрыть плеер
              </button>
            </div>
          </div>
          
          <div v-if="roomRecordsLoading" class="flex justify-center p-6">
            <HeartbeatLoader />
            <span class="ml-2">Загружаем записи...</span>
          </div>

          <div v-else-if="mediaAccessNotice.visible" class="text-center p-8">
            <div class="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h3 class="text-lg font-medium text-gray-900 mb-2">{{ mediaAccessNotice.title }}</h3>
            <p class="text-gray-600 mb-5">{{ mediaAccessNotice.description }}</p>
            <div class="flex justify-center gap-3">
              <button
                v-if="mediaAccessNotice.canRequest"
                @click="requestMediaAccessForSelectedAppointment"
                :disabled="mediaAccessNotice.requestSending"
                class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
              >
                {{ mediaAccessNotice.requestSending ? 'Отправляем запрос...' : 'Запросить доступ к видео и чату' }}
              </button>
              <button
                @click="showRoomRecords(selectedAppointmentForRecords)"
                class="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Проверить статус
              </button>
            </div>
          </div>
          
          <div v-else-if="roomRecordsError" class="text-center p-8">
            <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h3 class="text-lg font-medium text-gray-900 mb-2">{{ $t('patientDetails.records.error') }}</h3>
            <p class="text-red-600 mb-4">{{ roomRecordsError }}</p>
            <button 
              @click="showRoomRecords(selectedAppointmentForRecords)"
              class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {{ $t('common.retry') }}
            </button>
          </div>
          
          <div v-else-if="roomRecords.length > 0">
            <div class="flex items-center justify-between mb-4">
              <p class="text-gray-700">
                {{ $t('patientDetails.records.foundRecords') }}: {{ roomRecords.length }}
              </p>
              <div class="text-sm text-gray-500">
                <svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                {{ $t('patientDetails.records.clickToPlay') }}
              </div>
            </div>
            
            <div class="grid grid-cols-1 gap-3">
              <div 
                v-for="(record, index) in roomRecords" 
                :key="`record-${index}`" 
                class="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center">
                    <div class="w-12 h-12 bg-gradient-to-br from-purple-100 to-blue-100 rounded-lg flex items-center justify-center mr-4">
                      <svg class="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd"></path>
                      </svg>
                    </div>
                    <div>
                      <h4 class="font-medium text-gray-900">Запись встречи {{ index + 1 }}</h4>
                      <p class="text-sm text-gray-500">Видеофайл в формате MP4</p>
                      <div class="flex items-center mt-1 text-xs text-gray-400">
                        <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                        </svg>
                        {{ $t('patientDetails.records.readyToView') }}
                      </div>
                    </div>
                  </div>
                  
                  <div class="flex space-x-2">
                    <button 
                      @click="playVideo(record)"
                      class="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center"
                    >
                      <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd"></path>
                      </svg>
                      {{ $t('common.view') }}
                    </button>
                    <a 
                      :href="record"
                      target="_blank"
                      class="px-4 py-2 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors flex items-center"
                    >
                      <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                      </svg>
                      {{ $t('common.open') }}
                    </a>
                    <a 
                      :href="record"
                      :download="`meeting-record-${index + 1}.mp4`"
                      class="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center"
                    >
                      <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                      </svg>
                      {{ $t('common.download') }}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div v-else class="text-center p-8">
            <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
              </svg>
            </div>
            <h3 class="text-lg font-medium text-gray-900 mb-2">{{ $t('patientDetails.records.notFound') }}</h3>
            <p class="text-gray-600">{{ $t('patientDetails.records.noRecordsAvailable') }}</p>
          </div>
        </div>
        
        <div class="px-6 py-3 border-t bg-gray-50 flex justify-end rounded-b-lg">
          <button 
            @click="closeRoomRecordsDialog"
            class="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {{ $t('common.close') }}
          </button>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Appointment Conclusion Modal -->
  <div v-if="showConclusionModal" 
       class="fixed inset-0 z-[70] overflow-y-auto"
       :class="{'pointer-events-none': dualModeActive}">
    <!-- Backdrop overlay -->
    <div v-if="!dualModeActive" class="fixed inset-0 bg-black bg-opacity-40" @click="closeConclusionModal"></div>
    
    <!-- Modal dialog -->
    <div class="flex items-start justify-center min-h-screen pt-8 px-4 transition-all duration-300"
         :class="{'!justify-end !p-4 !pt-4 h-full': dualModeActive}">
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-5xl mx-auto z-10 max-h-[90vh] overflow-hidden flex flex-col transition-all duration-300 pointer-events-auto"
           :class="{'!w-[40%] !max-w-none !mx-0 !h-full': dualModeActive}">
        <div class="px-8 py-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div class="flex items-center justify-between">
            <div>
              <h2 class="text-2xl font-bold text-gray-900 mb-1">
                {{ $t('medical.conclusion.appointmentConclusion') }}
              </h2>
              <p class="text-gray-600 text-sm">
                {{ selectedAppointmentForConclusion ? getPatientName(selectedAppointmentForConclusion) : '' }}
              </p>
            </div>
            <div class="flex items-center space-x-2">
              <!-- Video warning indicator -->
              <div v-if="isVideoActive" class="flex items-center space-x-2 px-3 py-1 bg-yellow-100 rounded-full mr-2">
                <svg class="w-4 h-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                </svg>
                <span class="text-yellow-800 text-xs font-medium">Видео активно</span>
              </div>
              
              <button 
                @click="closeConclusionModal"
                class="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
        
        <div class="flex-1 overflow-y-auto">
          <div class="p-8 space-y-8">
            <!-- Основная информация -->
            <div class="grid grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-semibold text-gray-800 mb-3">Жалобы</label>
                <textarea 
                  v-model="conclusionForm.complaints"
                  class="w-full h-24 rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all resize-none"
                  :placeholder="$t('medical.conclusion.enterComplaints')"
                ></textarea>
              </div>
              <div>
                <label class="block text-sm font-semibold text-gray-800 mb-3">Анамнез заболевания</label>
                <textarea 
                  v-model="conclusionForm.disease_history"
                  class="w-full h-24 rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all resize-none"
                  :placeholder="$t('medical.conclusion.enterDiseaseHistory')"
                ></textarea>
              </div>
            </div>

            <!-- Анамнез жизни -->
            <div class="bg-gray-50 rounded-2xl p-6">
              <h3 class="text-lg font-bold text-gray-900 mb-6">{{ $t('medical.conclusion.lifeHistory') }}</h3>
              
              <div class="space-y-4">
                <!-- Состоит на "Д" учете -->
                <div class="flex items-center space-x-4">
                  <div class="w-44 flex-shrink-0">
                    <label class="text-sm font-medium text-gray-700">{{ $t('medical.conclusion.dRegistration') }}</label>
                  </div>
                  <div class="flex items-center space-x-3">
                    <label class="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        :checked="conclusionForm.d_registration === 'true'"
                        @change="conclusionForm.d_registration = $event.target.checked ? 'true' : 'false'"
                        class="sr-only peer"
                      >
                      <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                    </label>
                    <span class="text-sm font-medium text-gray-600">{{ conclusionForm.d_registration === 'true' ? $t('medical.conclusion.yes') : $t('medical.conclusion.no') }}</span>
                  </div>
                  <div class="flex-1">
                    <input 
                      v-model="conclusionForm.d_registration_details"
                      type="text" 
                      class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                      :placeholder="$t('medical.conclusion.additionalData')"
                    >
                  </div>
                </div>

                <!-- TBS -->
                <div class="flex items-center space-x-4">
                  <div class="w-44 flex-shrink-0">
                    <label class="text-sm font-medium text-gray-700">{{ $t('medical.conclusion.tbs') }}</label>
                  </div>
                  <div class="flex items-center space-x-3">
                    <label class="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        :checked="conclusionForm.tbs === 'true'"
                        @change="conclusionForm.tbs = $event.target.checked ? 'true' : 'false'"
                        class="sr-only peer"
                      >
                      <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                    </label>
                    <span class="text-sm font-medium text-gray-600">{{ conclusionForm.tbs === 'true' ? $t('medical.conclusion.yes') : $t('medical.conclusion.no') }}</span>
                  </div>
                  <div class="flex-1">
                    <input 
                      v-model="conclusionForm.tbs_details"
                      type="text" 
                      class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                      :placeholder="$t('medical.conclusion.additionalData')"
                    >
                  </div>
                </div>

                <!-- Аллергоанамнез -->
                <div class="flex items-center space-x-4">
                  <div class="w-44 flex-shrink-0">
                    <label class="text-sm font-medium text-gray-700">{{ $t('medical.conclusion.allergies') }}</label>
                  </div>
                  <div class="flex items-center space-x-3">
                    <label class="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        :checked="conclusionForm.allergies === 'true'"
                        @change="conclusionForm.allergies = $event.target.checked ? 'true' : 'false'"
                        class="sr-only peer"
                      >
                      <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                    </label>
                    <span class="text-sm font-medium text-gray-600">{{ conclusionForm.allergies === 'true' ? $t('medical.conclusion.yes') : $t('medical.conclusion.no') }}</span>
                  </div>
                  <div class="flex-1">
                    <input 
                      v-model="conclusionForm.allergies_details"
                      type="text" 
                      class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                      :placeholder="$t('medical.conclusion.additionalData')"
                    >
                  </div>
                </div>

                <!-- Вирусные гепатиты -->
                <div class="flex items-center space-x-4">
                  <div class="w-44 flex-shrink-0">
                    <label class="text-sm font-medium text-gray-700">{{ $t('medical.conclusion.viralHepatitis') }}</label>
                  </div>
                  <div class="flex items-center space-x-3">
                    <label class="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        :checked="conclusionForm.viral_hepatitis === 'true'"
                        @change="conclusionForm.viral_hepatitis = $event.target.checked ? 'true' : 'false'"
                        class="sr-only peer"
                      >
                      <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                    </label>
                    <span class="text-sm font-medium text-gray-600">{{ conclusionForm.viral_hepatitis === 'true' ? $t('medical.conclusion.yes') : $t('medical.conclusion.no') }}</span>
                  </div>
                  <div class="flex-1">
                    <input 
                      v-model="conclusionForm.viral_hepatitis_details"
                      type="text" 
                      class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                      :placeholder="$t('medical.conclusion.additionalData')"
                    >
                  </div>
                </div>
              </div>
            </div>

            <!-- Физикальные данные -->
            <div class="bg-blue-50 rounded-2xl p-6">
              <h3 class="text-lg font-bold text-gray-900 mb-6">{{ $t('medical.conclusion.physicalDataSection') }}</h3>
              
              <div class="grid grid-cols-4 gap-4 mb-6">
                <div class="bg-white rounded-lg p-4">
                  <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{{ $t('medical.conclusion.temperatureLabel') }}</label>
                  <input 
                    v-model="conclusionForm.temperature"
                    type="text" 
                    class="w-full text-lg font-medium text-gray-900 bg-transparent border-0 border-b-2 border-gray-200 focus:border-blue-400 focus:ring-0 px-0 py-1"
                    placeholder="36.6°C"
                  >
                </div>
                <div class="bg-white rounded-lg p-4">
                  <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{{ $t('medical.conclusion.bloodPressureLabel') }}</label>
                  <input 
                    v-model="conclusionForm.blood_pressure"
                    type="text" 
                    class="w-full text-lg font-medium text-gray-900 bg-transparent border-0 border-b-2 border-gray-200 focus:border-blue-400 focus:ring-0 px-0 py-1"
                    placeholder="120/80"
                  >
                </div>
                <div class="bg-white rounded-lg p-4">
                  <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{{ $t('medical.conclusion.pulseLabel') }}</label>
                  <input 
                    v-model="conclusionForm.pulse"
                    type="text" 
                    class="w-full text-lg font-medium text-gray-900 bg-transparent border-0 border-b-2 border-gray-200 focus:border-blue-400 focus:ring-0 px-0 py-1"
                    placeholder="70 уд/мин"
                  >
                </div>
                <div class="bg-white rounded-lg p-4">
                  <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{{ $t('medical.conclusion.respiratoryRateLabel') }}</label>
                  <input 
                    v-model="conclusionForm.respiratory_rate"
                    type="text" 
                    class="w-full text-lg font-medium text-gray-900 bg-transparent border-0 border-b-2 border-gray-200 focus:border-blue-400 focus:ring-0 px-0 py-1"
                    placeholder="16/мин"
                  >
                </div>
              </div>

              <div class="space-y-4">
                <!-- Отеки -->
                <div class="flex items-center space-x-4">
                  <div class="w-40 flex-shrink-0">
                    <label class="text-sm font-medium text-gray-700">{{ $t('medical.conclusion.edema') }}</label>
                  </div>
                  <div class="flex items-center space-x-3">
                    <label class="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        :checked="conclusionForm.edema === 'true'"
                        @change="conclusionForm.edema = $event.target.checked ? 'true' : 'false'"
                        class="sr-only peer"
                      >
                      <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                    </label>
                    <span class="text-sm font-medium text-gray-600">{{ conclusionForm.edema === 'true' ? $t('medical.conclusion.yes') : $t('medical.conclusion.no') }}</span>
                  </div>
                  <div class="flex-1">
                    <input 
                      v-model="conclusionForm.edema_details"
                      type="text" 
                      class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                      :placeholder="$t('medical.conclusion.additionalDataPlaceholder')"
                    >
                  </div>
                </div>

                <!-- Кожный покров -->
                <div class="flex items-center space-x-4">
                  <div class="w-40 flex-shrink-0">
                    <label class="text-sm font-medium text-gray-700">{{ $t('medical.conclusion.skinCondition') }}</label>
                  </div>
                  <div class="flex items-center space-x-3">
                    <label class="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        :checked="conclusionForm.skin_condition === 'true'"
                        @change="conclusionForm.skin_condition = $event.target.checked ? 'true' : 'false'"
                        class="sr-only peer"
                      >
                      <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                    </label>
                    <span class="text-sm font-medium text-gray-600">{{ conclusionForm.skin_condition === 'true' ? $t('medical.conclusion.yes') : $t('medical.conclusion.no') }}</span>
                  </div>
                  <div class="flex-1">
                    <input 
                      v-model="conclusionForm.skin_condition_details"
                      type="text" 
                      class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                      :placeholder="$t('medical.conclusion.additionalDataPlaceholder')"
                    >
                  </div>
                </div>

                <!-- Мочеиспускание и Стул -->
                <div class="grid grid-cols-2 gap-6">
                  <div class="bg-white rounded-lg p-4">
                    <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{{ $t('medical.conclusion.urination') }}</label>
                    <select 
                      v-model="conclusionForm.urination"
                      class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                    >
                      <option value="">{{ $t('medical.conclusion.choose') }}</option>
                      <option value="норма">{{ $t('medical.conclusion.normal') }}</option>
                      <option value="затруднено">{{ $t('medical.conclusion.difficult') }}</option>
                      <option value="болезненно">{{ $t('medical.conclusion.painful') }}</option>
                      <option value="учащено">{{ $t('medical.conclusion.frequent') }}</option>
                    </select>
                  </div>
                  <div class="bg-white rounded-lg p-4">
                    <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{{ $t('medical.conclusion.stool') }}</label>
                    <select 
                      v-model="conclusionForm.stool"
                      class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                    >
                      <option value="">{{ $t('medical.conclusion.choose') }}</option>
                      <option value="норма">{{ $t('medical.conclusion.normal') }}</option>
                      <option value="запор">{{ $t('medical.conclusion.constipation') }}</option>
                      <option value="понос">{{ $t('medical.conclusion.diarrhea') }}</option>
                      <option value="неустойчивый">{{ $t('medical.conclusion.unstable') }}</option>
                    </select>
                  </div>
                </div>

                <!-- Выделение из половых органов -->
                <div class="flex items-center space-x-4">
                  <div class="w-44 flex-shrink-0">
                    <label class="text-sm font-medium text-gray-700">{{ $t('medical.conclusion.genitalDischarge') }}</label>
                  </div>
                  <div class="flex items-center space-x-3">
                    <label class="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        :checked="conclusionForm.genital_discharge === 'true'"
                        @change="conclusionForm.genital_discharge = $event.target.checked ? 'true' : 'false'"
                        class="sr-only peer"
                      >
                      <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                    </label>
                    <span class="text-sm font-medium text-gray-600">{{ conclusionForm.genital_discharge === 'true' ? $t('medical.conclusion.yes') : $t('medical.conclusion.no') }}</span>
                  </div>
                  <div class="flex-1">
                    <input 
                      v-model="conclusionForm.genital_discharge_details"
                      type="text" 
                      class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                      :placeholder="$t('medical.conclusion.additionalData')"
                    >
                  </div>
                </div>
              </div>
            </div>

            <!-- Назначения и рекомендации -->
            <div class="grid grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-semibold text-gray-800 mb-3">{{ $t('medical.conclusion.examinations') }}</label>
                <textarea 
                  v-model="conclusionForm.examinations"
                  class="w-full h-32 rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all resize-none"
                                      :placeholder="$t('medical.conclusion.enterExaminations')"
                ></textarea>
              </div>
              <div>
                <label class="block text-sm font-semibold text-gray-800 mb-3">{{ $t('medical.conclusion.recommendations') }}</label>
                <textarea 
                  v-model="conclusionForm.recommendations"
                  class="w-full h-32 rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all resize-none"
                  :placeholder="$t('medical.conclusion.enterRecommendations')"
                ></textarea>
              </div>
            </div>

            <!-- Финальный протокол -->
            <div class="bg-green-50 rounded-2xl p-6">
              <div class="flex items-center justify-between mb-4">
                <label class="block text-lg font-bold text-gray-900">{{ $t('medical.conclusion.finalProtocol') }}</label>
                <button 
                  @click="updateFinalProtocol"
                  class="px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  type="button"
                >
                  {{ $t('medical.conclusion.updateProtocol') }}
                </button>
              </div>
              <textarea 
                v-model="conclusionForm.final_protocol"
                class="w-full h-40 rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-green-400 focus:ring-2 focus:ring-green-100 transition-all resize-none bg-white"
                placeholder="Протокол генерируется автоматически на основе заполненных полей..."
                readonly
              ></textarea>
              <p class="text-xs text-gray-500 mt-2">
                <svg class="w-3 h-3 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
                </svg>
                {{ $t('medical.conclusion.protocolAutoGenerated') }}
              </p>
            </div>
          </div>
        </div>
        
        <div class="px-8 py-6 border-t border-gray-100 bg-white flex justify-between items-center rounded-b-2xl">
          <button 
            @click="copyProtocol"
            class="flex items-center space-x-2 px-6 py-3 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
            </svg>
            <span class="font-medium">{{ $t('medical.conclusion.copyConclusion') }}</span>
          </button>
          
          <div class="flex space-x-4">
            <button 
            @click="saveConclusion"
            :disabled="conclusionLoading || isVideoActive"
            class="px-6 py-3 text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-all duration-200 font-medium shadow-lg shadow-blue-200 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            :title="isVideoActive ? 'Сначала завершите видеозвонок' : ''"
          >
            <HeartbeatLoader v-if="conclusionLoading" class="mr-2 w-5 h-5 text-white" />
            {{ $t('common.save') }}
          </button> 
            <button 
              @click="closeConclusionModal"
              class="px-6 py-3 text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-200 font-medium"
            >
              {{ $t('medical.conclusion.cancelConclusion') }}
            </button>
            <button 
              @click="saveAppointmentConclusion"
              class="flex items-center space-x-2 px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              :disabled="conclusionLoading"
              :class="{'opacity-50 cursor-not-allowed transform-none': conclusionLoading}"
            >
              <svg v-if="conclusionLoading" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
              </svg>
              <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
              </svg>
                              <span class="font-medium">{{ conclusionLoading ? $t('common.saving') : $t('appointments.completeAppointment') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Image Modal -->
  <div v-if="showImageModal" class="fixed inset-0 z-50 overflow-y-auto">
    <!-- Backdrop overlay -->
    <div class="fixed inset-0 bg-black opacity-75" @click="closeImageModal"></div>
    
    <!-- Modal dialog -->
    <div class="flex items-center justify-center min-h-screen p-4">
      <div class="bg-white rounded-lg shadow-xl max-w-4xl max-h-full mx-auto z-10 relative">
        <div class="p-4">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-medium text-gray-900">{{ modalImageAlt || $t('common.image') }}</h3>
            <button 
              @click="closeImageModal"
              class="text-gray-400 hover:text-gray-600"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          <div class="max-h-96 overflow-auto">
            <img 
              :src="modalImageUrl" 
              :alt="modalImageAlt"
              class="max-w-full h-auto rounded"
            />
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Toast Notifications -->
  <div v-if="toasts.length > 0" class="fixed top-4 right-4 z-50 space-y-2">
    <div 
      v-for="toast in toasts" 
      :key="toast.id"
      class="max-w-sm bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden transform transition-all duration-300 ease-in-out"
      :class="{
        'translate-x-0 opacity-100': toast.show,
        'translate-x-full opacity-0': !toast.show
      }"
    >
      <div class="flex items-center p-4">
        <div class="flex-shrink-0">
          <div v-if="toast.type === 'success'" class="w-6 h-6 text-green-600">
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div v-else-if="toast.type === 'error'" class="w-6 h-6 text-red-600">
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div v-else class="w-6 h-6 text-blue-600">
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
            </svg>
          </div>
        </div>
        <div class="ml-3 flex-1">
          <p class="text-sm font-medium text-gray-900">{{ toast.title }}</p>
          <p v-if="toast.message" class="text-sm text-gray-600 mt-1">{{ toast.message }}</p>
        </div>
        <div class="ml-4 flex-shrink-0">
          <button 
            @click="removeToast(toast.id)"
            class="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Consilium Invitation Dialog -->
  <div v-if="showConsiliumDialog" class="fixed inset-0 z-50 overflow-y-auto">
    <!-- Backdrop overlay -->
    <div class="fixed inset-0 bg-black opacity-50" @click="closeConsiliumDialog"></div>
    
    <!-- Modal dialog -->
    <div class="flex items-center justify-center min-h-screen p-4">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-md mx-auto z-10">
        <div class="px-6 py-4 border-b">
          <h2 class="text-xl font-semibold text-gray-800">{{ $t('consilium.inviteTitle') }}</h2>
        </div>
        
        <div class="p-6">
          <div v-if="consiliumDoctorsLoading" class="flex justify-center p-4">
            <HeartbeatLoader :text="$t('common.loading')" />
          </div>
          
          <div v-else-if="consiliumDoctorsError" class="p-4 bg-red-50 text-red-600 rounded-md">
            {{ consiliumDoctorsError }}
          </div>
          
          <div v-else>
            <p class="mb-4 text-gray-700">
              {{ $t('consilium.inviteDescription') }}
            </p>
            
            <div v-if="availableConsiliumDoctors.length === 0" class="p-4 bg-gray-50 text-gray-600 rounded-md">
              {{ $t('consilium.noDoctorsAvailable') }}
            </div>
            
            <div v-else class="space-y-2 max-h-60 overflow-y-auto">
              <div v-for="doctor in availableConsiliumDoctors" :key="doctor.id" 
                   class="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-md">
                <input 
                  type="checkbox" 
                  :id="`doctor-${doctor.id}`"
                  v-model="selectedConsiliumDoctors"
                  :value="doctor.id"
                  class="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                >
                <label :for="`doctor-${doctor.id}`" class="flex-1 cursor-pointer">
                  <div class="font-medium text-gray-900">{{ doctor.user.full_name }} - {{ formatDoctorSpecialization(doctor) || doctor.specialization }}</div>
                </label>
              </div>
            </div>
          </div>
        </div>
        
        <div class="px-6 py-3 border-t bg-gray-50 flex justify-between rounded-b-lg">
          <button 
            @click="closeConsiliumDialog"
            class="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {{ $t('common.cancel') }}
          </button>
          
          <button 
            @click="inviteToConsilium"
            class="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
            :disabled="consiliumInviteLoading || selectedConsiliumDoctors.length === 0"
            :class="{'opacity-50 cursor-not-allowed': consiliumInviteLoading || selectedConsiliumDoctors.length === 0}"
          >
            {{ consiliumInviteLoading ? $t('common.loading') : $t('consilium.inviteButton') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Модальное окно видеокомнаты -->
  <VideoRoomModal
    :show="showVideoRoomModal"
    :roomUrl="currentVideoRoomUrl"
    :appointment="currentAppointment"
    :dualMode="dualModeActive"
    @close="closeVideoRoomModal"
    @open-conclusion="openConclusionFromVideo"
    @video-active-changed="handleVideoActiveChanged"
  />
</template>

<script>
import api from '../services/api'
import staffApi from '../services/staffApi'
import organizationsApi from '../services/organizationsApi'
import HeartbeatLoader from '../components/common/HeartbeatLoader.vue'
import VideoRoomModal from '../components/common/VideoRoomModal.vue'
import { formatDoctorSpecialization as formatDoctorSpecializationText } from '../utils/doctorDisplay'

export default {
  name: 'AppointmentsList',
  components: {
    HeartbeatLoader,
    VideoRoomModal
  },
  props: {
    mode: {
      type: String,
      default: 'doctor'
    }
  },
  data() {
    return {
      appointmentsScope: 'mine',
      activeTab: 'upcoming',
      appointments: [],
      loading: false,
      error: null,
      currentUser: null, // Добавляем данные текущего пользователя
      currentStaffRole: null,
      managerClinicId: null,
      managerClinicType: null,
      managerDoctors: [],
      selectedManagerDoctor: '',
      organizationMemberships: [],
      selectedOrganizationId: '',
      slotsData: {},
      patientsData: {},
      doctorsData: {},
      nursesData: {},
      visitReasonsData: {},
      tabs: [
        { key: 'today', label: this.$t('common.today') },
        { key: 'upcoming', label: this.$t('appointment.upcomingAppointments') },
        { key: 'past', label: this.$t('appointment.pastAppointments') }
      ],
      // Room records dialog
      showRoomRecordsDialog: false,
      roomRecordsLoading: false,
      roomRecords: [],
      selectedAppointmentForRecords: null,
      roomRecordsError: null,
      mediaAccessNotice: {
        visible: false,
        status: null,
        title: '',
        description: '',
        canRequest: false,
        requestSending: false
      },
      // Video player state
      selectedVideoUrl: null,
      showVideoPlayer: false,
      // Dual mode state
      currentAppointment: null,
      isVideoActive: false,
      dualModeActive: false,
      // Chat state
      roomChatMessages: [],
      roomChatLoading: false,
      roomChatError: null,
      showChatPanel: false,
      // Image modal state
      showImageModal: false,
      modalImageUrl: null,
      modalImageAlt: null,
      // Conclusion modal state
      showConclusionModal: false,
      selectedAppointmentForConclusion: null,
      conclusionLoading: false,
      conclusionForm: {
        complaints: '',
        disease_history: '',
        d_registration: '',
        d_registration_details: '',
        tbs: '',
        tbs_details: '',
        allergies: '',
        allergies_details: '',
        viral_hepatitis: '',
        viral_hepatitis_details: '',
        temperature: '',
        blood_pressure: '',
        pulse: '',
        respiratory_rate: '',
        edema: '',
        edema_details: '',
        skin_condition: '',
        skin_condition_details: '',
        urination: '',
        stool: '',
        genital_discharge: '',
        genital_discharge_details: '',
        examinations: '',
        recommendations: '',
        final_protocol: ''
      },
      toasts: [],
      // Consilium dialog
      showConsiliumDialog: false,
      selectedAppointmentForConsilium: null,
      availableConsiliumDoctors: [],
      selectedConsiliumDoctors: [],
      consiliumDoctorsLoading: false,
      consiliumDoctorsError: null,
      consiliumInviteLoading: false,
      // Date range filter
      dateRange: {
        startDate: '',
        endDate: ''
      },
      // Video room modal
      showVideoRoomModal: false,
      currentVideoRoomUrl: '',
      currentDoctorProfile: null,
      isCommercialClinicDoctor: false,
      showVisitReasonsConfigModal: false,
      visitReasonsConfigLoading: false,
      visitReasonsConfigSaving: false,
      visitReasonsConfigError: null,
      visitReasonsConfigRows: [],
      allVisitReasonsForConfig: []
    }
  },
  computed: {
    isClinicManagerMode() {
      return this.mode === 'manager'
    },
    pageTitle() {
      return this.isClinicManagerMode ? 'Приемы поликлиники' : this.$t('appointment.appointments')
    },
    hasOrganizationAccess() {
      if (this.isClinicManagerMode) return false
      return Array.isArray(this.organizationMemberships) && this.organizationMemberships.length > 0
    },
    isOrganizationScope() {
      if (this.isClinicManagerMode) return false
      return this.appointmentsScope === 'organization'
    },
    currentAppointments() {
      let filteredAppointments = [...this.appointments]
      
      // Применяем фильтр по датам если выбран диапазон
      if (this.dateRange.startDate || this.dateRange.endDate) {
        filteredAppointments = filteredAppointments.filter(appointment => {
          const appointmentDate = this.getAppointmentDate(appointment)
          if (!appointmentDate) return true
          
          const dateObj = new Date(appointmentDate)
          const dateString = dateObj.toISOString().split('T')[0] // Приводим к формату YYYY-MM-DD
          
          let passesFilter = true
          
          if (this.dateRange.startDate) {
            passesFilter = passesFilter && dateString >= this.dateRange.startDate
          }
          
          if (this.dateRange.endDate) {
            passesFilter = passesFilter && dateString <= this.dateRange.endDate
          }
          
          return passesFilter
        })
      }
      
      return filteredAppointments
    }
  },
  watch: {
    activeTab() {
      this.fetchAppointments()
    },
    appointmentsScope() {
      this.fetchAppointments()
    },
    selectedManagerDoctor() {
      if (this.isClinicManagerMode) {
        this.fetchAppointments()
      }
    },
    selectedOrganizationId() {
      if (this.appointmentsScope === 'organization') {
        this.fetchAppointments()
      }
    },
    // Отслеживаем изменения в диапазоне дат для корректной реактивности
    'dateRange.startDate'() {
    },
    'dateRange.endDate'() {
    }
  },
  async created() {
    await this.fetchCurrentUser()
    if (this.isClinicManagerMode) {
      await this.loadClinicManagerDoctors()
      await this.fetchVisitReasons()
      this.fetchAppointments()
      return
    }

    await this.fetchDoctorOrganizations()
    this.fetchAppointments()
    this.fetchVisitReasons()
  },
  methods: {
    formatDoctorSpecialization(doctor) {
      return formatDoctorSpecializationText(doctor)
    },
    async fetchCurrentUser() {
      try {
        const authResponse = await api.checkAuth()
        const authUser = authResponse.data.user || null
        this.currentUser = authUser
        this.currentStaffRole = authUser?.staff_role || null

        const assignedClinic = authUser?.assigned_clinic
        if (assignedClinic && typeof assignedClinic === 'object') {
          this.managerClinicId = assignedClinic.id || null
          this.managerClinicType = assignedClinic.clinic_type || null
        } else {
          this.managerClinicId = assignedClinic || null
        }
      } catch (error) {
      // Error handled silently
      }
    },
    isDoctorUser() {
      if (!this.currentUser) return false

      if (Array.isArray(this.currentUser.types)) {
        return this.currentUser.types.includes('doctor')
      }

      return this.currentUser.type === 'doctor'
    },
    getDoctorDisplayName(doctor) {
      if (!doctor) return 'Врач'

      if (doctor.name) return doctor.name
      if (doctor.user?.full_name) return doctor.user.full_name
      if (doctor.full_name) return doctor.full_name

      const parts = []
      if (doctor.user?.last_name) parts.push(doctor.user.last_name)
      if (doctor.user?.first_name) parts.push(doctor.user.first_name)
      if (doctor.user?.middle_name) parts.push(doctor.user.middle_name)
      return parts.join(' ') || `Врач #${doctor.id}`
    },
    getAppointmentDoctorId(appointment) {
      if (!appointment || !appointment.doctor) return null

      if (typeof appointment.doctor === 'number') {
        return appointment.doctor
      }

      if (typeof appointment.doctor === 'string' && !Number.isNaN(Number(appointment.doctor))) {
        return Number(appointment.doctor)
      }

      if (typeof appointment.doctor === 'object') {
        return appointment.doctor.id || null
      }

      return null
    },
    extractMedicalStaff(responsePayload) {
      if (!responsePayload) return []
      if (Array.isArray(responsePayload)) return responsePayload
      if (Array.isArray(responsePayload.medical_staff)) return responsePayload.medical_staff
      if (Array.isArray(responsePayload.results)) return responsePayload.results
      if (Array.isArray(responsePayload.data?.medical_staff)) return responsePayload.data.medical_staff
      if (Array.isArray(responsePayload.data?.results)) return responsePayload.data.results
      return []
    },
    filterManagerAppointments(appointments) {
      const doctorIds = new Set(this.managerDoctors.map((doctor) => Number(doctor.id)))

      if (doctorIds.size === 0) {
        return []
      }

      let filtered = appointments.filter((appointment) => {
        const doctorId = this.getAppointmentDoctorId(appointment)
        if (!doctorId) return false

        return doctorIds.has(Number(doctorId))
      })

      if (this.selectedManagerDoctor) {
        const selectedId = Number(this.selectedManagerDoctor)
        filtered = filtered.filter((appointment) => Number(this.getAppointmentDoctorId(appointment)) === selectedId)
      }

      return filtered
    },
    async loadClinicManagerDoctors() {
      this.managerDoctors = []

      if (!this.currentUser || this.currentStaffRole !== 'clinic_manager') return

      try {
        const response = await staffApi.getMedicalStaff({ type: 'doctor' })
        const medicalStaff = this.extractMedicalStaff(response)

        this.managerDoctors = medicalStaff
          .filter((staffMember) => !staffMember.type || staffMember.type === 'doctor')
          .map((staffMember) => ({
            id: staffMember.id,
            name: staffMember.name,
            specialization: staffMember.specialization,
            clinic_name: staffMember.clinic_name
          }))
      } catch (error) {
        this.managerDoctors = []
      }

      if (this.selectedManagerDoctor) {
        const selectedExists = this.managerDoctors.some(
          (doctor) => Number(doctor.id) === Number(this.selectedManagerDoctor)
        )
        if (!selectedExists) {
          this.selectedManagerDoctor = ''
        }
      }

      if (!this.managerClinicType && this.managerClinicId) {
        try {
          const clinicResponse = await api.get(`/clinics/${this.managerClinicId}/`)
          this.managerClinicType = clinicResponse.data?.clinic_type || null
        } catch (error) {
          this.managerClinicType = null
        }
      }
    },
    async fetchDoctorCommercialState() {
      this.currentDoctorProfile = null
      this.isCommercialClinicDoctor = false

      if (!this.isDoctorUser()) return

      try {
        const response = await api.getDoctorProfile()
        this.currentDoctorProfile = response.data || null

        const clinicType = this.currentDoctorProfile?.clinic?.clinic_type
        this.isCommercialClinicDoctor = clinicType === 'commercial'
      } catch (error) {
        this.currentDoctorProfile = null
        this.isCommercialClinicDoctor = false
      }
    },
    buildVisitReasonsConfigRows(allReasons = [], doctorConfigs = []) {
      const configByReasonId = new Map()
      doctorConfigs.forEach((cfg) => {
        configByReasonId.set(cfg.visit_reason, cfg)
      })

      return allReasons.map((reason) => {
        const cfg = configByReasonId.get(reason.id)
        const selected = cfg ? Boolean(cfg.is_active) : false
        const price = cfg && cfg.price !== null && cfg.price !== undefined
          ? String(cfg.price)
          : '0.00'

        return {
          id: reason.id,
          name: reason.name || reason.name_ru || '',
          name_kk: reason.name_kk || '',
          description: reason.description || '',
          selected,
          price
        }
      })
    },
    async openVisitReasonsConfigModal() {
      if (!this.isCommercialClinicDoctor) return

      this.showVisitReasonsConfigModal = true
      this.visitReasonsConfigLoading = true
      this.visitReasonsConfigSaving = false
      this.visitReasonsConfigError = null

      try {
        const [allReasonsResponse, doctorConfigsResponse] = await Promise.all([
          api.getVisitReasons(),
          staffApi.getDoctorVisitReasonsConfig()
        ])

        const allReasons = Array.isArray(allReasonsResponse.data) ? allReasonsResponse.data : []
        const doctorConfigs = Array.isArray(doctorConfigsResponse.data) ? doctorConfigsResponse.data : []

        this.allVisitReasonsForConfig = allReasons
        this.visitReasonsConfigRows = this.buildVisitReasonsConfigRows(allReasons, doctorConfigs)
      } catch (error) {
        this.visitReasonsConfigError = 'Не удалось загрузить настройки причин и цен. Попробуйте еще раз.'
      } finally {
        this.visitReasonsConfigLoading = false
      }
    },
    closeVisitReasonsConfigModal() {
      if (this.visitReasonsConfigSaving) return

      this.showVisitReasonsConfigModal = false
      this.visitReasonsConfigError = null
    },
    handleVisitReasonSelection(row) {
      if (!row) return

      if (row.selected && (!row.price || Number(row.price) < 0)) {
        row.price = '0.00'
      }
    },
    isValidVisitReasonPrice(rawPrice) {
      if (rawPrice === '' || rawPrice === null || rawPrice === undefined) return false

      const price = Number(rawPrice)
      return Number.isFinite(price) && price >= 0
    },
    normalizeVisitReasonPrice(rawPrice) {
      return Number(rawPrice).toFixed(2)
    },
    onVisitReasonPriceBlur(row) {
      if (!row || !row.selected) return

      if (this.isValidVisitReasonPrice(row.price)) {
        row.price = this.normalizeVisitReasonPrice(row.price)
      }
    },
    async saveVisitReasonsConfig() {
      const selectedRows = this.visitReasonsConfigRows.filter((row) => row.selected)

      const invalidRow = selectedRows.find((row) => !this.isValidVisitReasonPrice(row.price))
      if (invalidRow) {
        this.visitReasonsConfigError = `Укажите корректную цену для причины: ${this.getLocalizedVisitReasonName(invalidRow)}`
        return
      }

      const payload = {
        configs: selectedRows.map((row) => ({
          visit_reason_id: row.id,
          price: this.normalizeVisitReasonPrice(row.price),
          is_active: true
        }))
      }

      this.visitReasonsConfigSaving = true
      this.visitReasonsConfigError = null

      try {
        const response = await staffApi.updateDoctorVisitReasonsConfig(payload)
        const updatedConfigs = Array.isArray(response.data) ? response.data : []
        this.visitReasonsConfigRows = this.buildVisitReasonsConfigRows(
          this.allVisitReasonsForConfig,
          updatedConfigs
        )
        this.showNotification('Настройки причин приема и цен успешно сохранены', 'success')
        this.showVisitReasonsConfigModal = false
      } catch (error) {
        const serverDetail = error.response?.data?.detail
        this.visitReasonsConfigError = serverDetail || 'Не удалось сохранить настройки причин и цен.'
      } finally {
        this.visitReasonsConfigSaving = false
      }
    },
    async fetchDoctorOrganizations() {
      try {
        const response = await organizationsApi.getDoctorOrganizations()
        this.organizationMemberships = response.results || response || []
      } catch (error) {
        this.organizationMemberships = []
      }
    },
    async fetchAppointments() {
      this.loading = true
      this.error = null
      
      try {
        if (this.isClinicManagerMode && this.managerDoctors.length === 0) {
          this.appointments = []
          await this.fetchAppointmentDetails()
          return
        }

        let response;
        if (this.isClinicManagerMode) {
          if (this.activeTab === 'upcoming') {
            response = await staffApi.getStaffUpcomingAppointments()
          } else if (this.activeTab === 'today') {
            response = await staffApi.getStaffTodayAppointments()
          } else if (this.activeTab === 'past') {
            response = await staffApi.getStaffPastAppointments()
          } else {
            response = await staffApi.getStaffAppointments({ status: this.activeTab })
          }
        } else if (this.isOrganizationScope) {
          const params = { period: this.activeTab }
          if (this.selectedOrganizationId) {
            params.organization_id = this.selectedOrganizationId
          }
          response = await staffApi.getStaffOrganizationAppointments(params)
        } else {
          // Используем специальные эндпоинты для upcoming/today/past вместо параметра status
          if (this.activeTab === 'upcoming') {
            response = await staffApi.getStaffUpcomingAppointments();
          } else if (this.activeTab === 'today') {
            response = await staffApi.getStaffTodayAppointments();
          } else if (this.activeTab === 'past') {
            response = await staffApi.getStaffPastAppointments();
          } else {
            const params = { status: this.activeTab };
            response = await staffApi.getStaffAppointments(params);
          }
        }
        const rawAppointments = response.data.results || response.data || []
        this.appointments = this.isClinicManagerMode
          ? this.filterManagerAppointments(rawAppointments)
          : rawAppointments
        
        await this.fetchAppointmentDetails()
      } catch (error) {
        this.error = 'Не удалось загрузить список приемов'
      } finally {
        this.loading = false
      }
    },
    
    async fetchAppointmentDetails() {
      this.slotsData = {}
      this.patientsData = {}
      this.doctorsData = {}
      this.nursesData = {}
      
      // Extract slot data from appointments (slots are now included in appointment response)
      this.appointments.forEach(appointment => {
        if (appointment.slot && typeof appointment.slot === 'object') {
          this.slotsData[appointment.slot.id] = appointment.slot;
        }
      });
      
      
      const patientIds = this.appointments.map(a => a.patient).filter(id => id)
      const doctorIds = this.appointments
        .map((appointment) => this.getAppointmentDoctorId(appointment))
        .filter((id) => Boolean(id))
      const nurseIds = this.appointments.map(a => a.nurse).filter(id => id)
      
      // Fetch patients
      for (const patientId of patientIds) {
        try {
          const patientResponse = await api.getPatientById(patientId)
          if (patientResponse.data) {
            this.patientsData[patientId] = patientResponse.data
          }
        } catch (error) {
      // Error handled silently
        }
      }
      
      // Fetch doctors
      for (const doctorId of doctorIds) {
        try {
          const doctorResponse = await api.getDoctorById(doctorId)
          if (doctorResponse.data) {
            this.doctorsData[doctorId] = doctorResponse.data
          }
        } catch (error) {
      // Error handled silently
        }
      }
      
      // Fetch nurses (if needed for display)
      for (const nurseId of nurseIds) {
        try {
          const nurseResponse = await api.getNurseById(nurseId)
          if (nurseResponse.data) {
            this.nursesData[nurseId] = nurseResponse.data
          }
        } catch (error) {
          // Fallback в случае ошибки
          this.nursesData[nurseId] = { user: { full_name: 'Медсестра' } }
        }
      }
    },
    
    // Новый метод для получения даты приема (слот или время создания)
    getAppointmentDate(appointment) {
      if (appointment.call_type === 'nurse_call') {
        // Для звонков медсестер используем время создания
        return appointment.booked_at || appointment.created_at
      } else {
        // Для приемов врачей используем дату слота
        // Slot может быть объектом или ID
        const slot = appointment.slot && typeof appointment.slot === 'object' ? appointment.slot : this.slotsData[appointment.slot]
        return slot ? slot.date : appointment.booked_at
      }
    },
    
    // Новый метод для получения времени начала приема
    getAppointmentStartTime(appointment) {
      if (appointment.call_type === 'nurse_call') {
        const date = new Date(appointment.booked_at || appointment.created_at)
        return date.toTimeString().substring(0, 5)
      } else {
        // Slot может быть объектом или ID
        const slot = appointment.slot && typeof appointment.slot === 'object' ? appointment.slot : this.slotsData[appointment.slot]
        return slot ? slot.start_time : ''
      }
    },
    
    // Новый метод для получения времени окончания приема
    getAppointmentEndTime(appointment) {
      if (appointment.call_type === 'nurse_call') {
        if (appointment.duration_minutes) {
          const start = new Date(appointment.booked_at || appointment.created_at)
          const end = new Date(start.getTime() + appointment.duration_minutes * 60000)
          return end.toTimeString().substring(0, 5)
        }
        return ''
      } else {
        // Slot может быть объектом или ID
        const slot = appointment.slot && typeof appointment.slot === 'object' ? appointment.slot : this.slotsData[appointment.slot]
        return slot ? slot.end_time : ''
      }
    },
    
    // Новый метод для получения имени врача или медсестры
    getProviderName(appointment) {
      if (appointment.call_type === 'nurse_call' && appointment.nurse) {
        const nurse = this.nursesData[appointment.nurse]
        if (nurse && nurse.user) {
          return this.formatUserName(nurse.user)
        }
        return 'Медсестра'
      } else if (appointment.doctor) {
        const doctor = this.doctorsData[appointment.doctor]
        if (doctor && doctor.user) {
          return this.formatUserName(doctor.user)
        }
        return 'Врач'
      }
      return 'Неизвестно'
    },
    
    formatUserName(user) {
      const parts = []
      if (user.last_name) parts.push(user.last_name)
      if (user.first_name) parts.push(user.first_name)
      if (user.middle_name) parts.push(user.middle_name)
      return parts.join(' ') || user.full_name || 'Пользователь'
    },
    
    // Оставляем старые методы для совместимости (они будут вызывать новые)
    getSlotDate(appointment) {
      return this.getAppointmentDate(appointment)
    },
    
    getSlotStartTime(appointment) {
      return this.getAppointmentStartTime(appointment)
    },
    
    getSlotEndTime(appointment) {
      return this.getAppointmentEndTime(appointment)
    },
    
    getPatientName(appointment) {
      // 1) Попытка получить имя из кэшированных данных пациента
      const patient = this.patientsData[appointment.patient]
      if (patient && patient.user) {
        return this.formatUserName(patient.user)
      }

      // 2) Если API уже вернул строку с ФИО пациента внутри объекта appointment
      if (appointment.patient_name) {
        return appointment.patient_name
      }

      // 3) Если в самом объекте appointment вложен объект patient с user.full_name
      if (appointment.patient && appointment.patient.user && appointment.patient.user.full_name) {
        return appointment.patient.user.full_name
      }

      // 4) Финальный резерв – выводим «Пациент #ID»
      if (appointment.patient) {
        return `Пациент #${appointment.patient}`
      }

      return 'Пациент'
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return '' // Проверка на Invalid Date
      return date.toLocaleDateString('ru-RU')
    },
    
    formatTime(timeString) {
      if (!timeString) return ''
      // If the string is already in format like "08:00:00"
      if (typeof timeString === 'string' && timeString.includes(':')) {
        return timeString.substring(0, 5); // Return first 5 characters (HH:MM)
      }
      // If it's a date object or date string, try to parse it
      try {
        const time = new Date(timeString);
        if (!isNaN(time.getTime())) {
          return time.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
        }
      } catch (e) {
      // Error handled silently
      }
      return '';
    },
    
    formatDateTime(dateTimeString) {
      if (!dateTimeString) return ''
      const date = new Date(dateTimeString)
      return date.toLocaleString('ru-RU')
    },
    
    getStatusText(status) {
      const statusMap = {
        scheduled: this.$t('appointment.scheduled'),
        in_progress: this.$t('appointment.inProgress'),
        completed: this.$t('appointment.completed'),
        cancelled: this.$t('appointment.cancelled'),
        no_show: this.$t('appointment.no_show'),
        patient_no_show: this.$t('appointment.patient_no_show')
      }
      return statusMap[status] || status
    },
    
    getStatusClass(status) {
      const classMap = {
        scheduled: 'bg-blue-100 text-blue-800',
        in_progress: 'bg-yellow-100 text-yellow-800',
        completed: 'bg-green-100 text-green-800',
        cancelled: 'bg-red-100 text-red-800',
        no_show: 'bg-red-100 text-red-800',
        patient_no_show: 'bg-orange-100 text-orange-800'
      }
      return classMap[status] || 'bg-gray-100 text-gray-800'
    },
    getEmptyAppointmentsMessage() {
      if (this.isClinicManagerMode) {
        if (this.activeTab === 'today') {
          return 'Приемы поликлиники на сегодня не найдены'
        }
        if (this.activeTab === 'upcoming') {
          return 'Предстоящие приемы поликлиники не найдены'
        }
        return 'Прошедшие приемы поликлиники не найдены'
      }

      if (this.activeTab === 'upcoming') {
        return this.$t('messages.noUpcomingAppointments')
      }
      if (this.activeTab === 'today') {
        return this.$t('messages.noTodayAppointments')
      }
      return this.$t('messages.noPastAppointments')
    },
    showBookingStatusForAppointment(appointment) {
      return this.isClinicManagerMode &&
        this.managerClinicType === 'commercial' &&
        appointment?.call_type === 'doctor_appointment'
    },
    getBookingStatusText(bookingStatus) {
      const statusMap = {
        pending: 'Не подтверждена',
        confirmed: 'Подтверждена',
        rejected: 'Отклонена'
      }
      return statusMap[bookingStatus] || 'Не подтверждена'
    },
    getBookingStatusClass(bookingStatus) {
      const classMap = {
        pending: 'bg-amber-100 text-amber-800',
        confirmed: 'bg-emerald-100 text-emerald-800',
        rejected: 'bg-rose-100 text-rose-800'
      }
      return classMap[bookingStatus] || 'bg-amber-100 text-amber-800'
    },
    canSetBookingStatus(appointment, nextStatus) {
      if (!this.showBookingStatusForAppointment(appointment)) return false
      const appointmentStatus = String(appointment?.status || '').trim().toLowerCase()
      if (!['scheduled', 'in_progress'].includes(appointmentStatus)) return false

      const currentStatus = String(appointment?.booking_status || 'pending').trim().toLowerCase()
      return currentStatus !== nextStatus
    },
    async setBookingStatus(appointment, bookingStatus) {
      if (!appointment?.id) return

      try {
        const response = await staffApi.updateAppointmentBookingStatus(appointment.id, bookingStatus)
        const updated = response.data || {}
        appointment.booking_status = updated.booking_status || bookingStatus
      } catch (error) {
        const detail = error.response?.data?.detail
        alert(detail || 'Не удалось изменить статус бронирования.')
      }
    },
    
    async startConsultation(appointment) {
      try {
        
        // Первый шаг: Создаем видеокомнату
        const videoRoomResponse = await api.createVideoRoom();
        
        if (!videoRoomResponse.data || videoRoomResponse.data.result !== 'success' || !videoRoomResponse.data.data) {
          throw new Error('Failed to create video room: Invalid API response');
        }
        
        const roomName = videoRoomResponse.data.data.room_name;
        const roomUrl = videoRoomResponse.data.data.url;
        
        
        // Второй шаг: Обновляем статус приема и сохраняем данные видеокомнаты
        const updateData = {
          status: 'in_progress',
          video_room_url: roomUrl,
          video_room_name: roomName
        };
        
        await staffApi.updateStaffAppointmentVideoRoom(appointment.id, updateData);
        
        // Обновляем локальные данные
        appointment.status = 'in_progress';
        appointment.video_room_url = roomUrl;
        appointment.video_room_name = roomName;
        
        // Открываем видеокомнату
        const { openVideoRoomWithLanguage } = await import('@/utils/videoRoom');
        openVideoRoomWithLanguage(roomUrl, this.$i18n.locale);
        
        // Обновляем список приемов
        await this.fetchAppointments();
        
      } catch (error) {
        // Показываем более детальное сообщение об ошибке
        if (error.message && error.message.includes('видеокомнату')) {
          alert(error.message);
        } else {
          alert('Произошла ошибка при начале приема. Пожалуйста, попробуйте позже.');
        }
      }
    },
    
    joinVideoCall(appointment) {
      if (appointment.video_room_url) {
        this.currentVideoRoomUrl = appointment.video_room_url
        this.currentAppointment = appointment
        this.showVideoRoomModal = true
        this.isVideoActive = true
      } else {
        alert(this.$t('appointment.videoRoomUnavailable'))
      }
    },
    closeVideoRoomModal() {
      this.showVideoRoomModal = false
      this.currentVideoRoomUrl = ''
      this.currentAppointment = null
      this.isVideoActive = false
      this.dualModeActive = false
    },
    openConclusionFromVideo() {
      if (this.currentAppointment) {
        this.dualModeActive = true
        this.openConclusionModal(this.currentAppointment)
      }
    },
    handleVideoActiveChanged(isActive) {
      this.isVideoActive = isActive
    },
    closeConclusionWhileDual() {
      this.showConclusionModal = false
      this.dualModeActive = false
    },
    
    async shareRoomLink(appointment) {
      try {
        if (!appointment.video_room_url) {
          this.showNotification(this.$t('appointment.roomLinkUnavailable'), 'error');
          return;
        }
        
        
        // Добавляем языковой параметр к URL
        const { addLanguageToVideoRoomUrl } = await import('@/utils/videoRoom');
        const urlWithLanguage = addLanguageToVideoRoomUrl(appointment.video_room_url, this.$i18n.locale);
        
        // Проверяем поддержку clipboard API
        if (navigator.clipboard && window.isSecureContext) {
          // Используем современный clipboard API
          await navigator.clipboard.writeText(urlWithLanguage);
          this.showNotification(this.$t('appointment.roomLinkCopied'), 'success');
        } else {
          // Fallback метод для старых браузеров или не-HTTPS
          await this.fallbackCopyToClipboard(urlWithLanguage);
          this.showNotification(this.$t('appointment.roomLinkCopied'), 'success');
        }
        
      } catch (error) {
        this.showNotification('Не удалось скопировать ссылку: ' + error.message, 'error');
      }
    },
    
    // Вспомогательный метод для fallback копирования
    async fallbackCopyToClipboard(text) {
      return new Promise((resolve, reject) => {
        let textArea;
        try {
          textArea = document.createElement('textarea');
          textArea.value = text;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          
          textArea.focus();
          textArea.select();
          
          const successful = document.execCommand('copy');
          document.body.removeChild(textArea);
          
          if (successful) {
            resolve();
          } else {
            reject(new Error('execCommand failed'));
          }
        } catch (error) {
          if (textArea && document.body.contains(textArea)) {
            document.body.removeChild(textArea);
          }
          reject(error);
        }
      });
    },

    // Consilium methods
    async openConsiliumDialog(appointment) {
      this.selectedAppointmentForConsilium = appointment;
      this.selectedConsiliumDoctors = [];
      this.showConsiliumDialog = true;
      
      // Load available doctors
      await this.loadConsiliumDoctors();
    },
    
    closeConsiliumDialog() {
      this.showConsiliumDialog = false;
      this.selectedAppointmentForConsilium = null;
      this.availableConsiliumDoctors = [];
      this.selectedConsiliumDoctors = [];
      this.consiliumDoctorsError = null;
    },
    
    async loadConsiliumDoctors() {
      this.consiliumDoctorsLoading = true;
      this.consiliumDoctorsError = null;
      
      try {
        const response = await api.getClinicDoctorsForConsilium();
        this.availableConsiliumDoctors = response.data || [];
      } catch (error) {
        this.consiliumDoctorsError = 'Не удалось загрузить список врачей клиники';
      } finally {
        this.consiliumDoctorsLoading = false;
      }
    },
    
    async inviteToConsilium() {
      if (!this.selectedAppointmentForConsilium || this.selectedConsiliumDoctors.length === 0) {
        return;
      }
      
      this.consiliumInviteLoading = true;
      
      try {
        await api.inviteToConsilium(
          this.selectedAppointmentForConsilium.id, 
          this.selectedConsiliumDoctors
        );
        
        // Show success notification
        this.showNotification(this.$t('consilium.inviteSuccess'), 'success');
        
        // Close dialog and refresh appointments
        this.closeConsiliumDialog();
        await this.fetchAppointments();
        
      } catch (error) {
        let errorMessage = this.$t('consilium.inviteError');
        
        if (error.response && error.response.data && error.response.data.detail) {
          errorMessage = error.response.data.detail;
        }
        
        this.showNotification(errorMessage, 'error');
      } finally {
        this.consiliumInviteLoading = false;
      }
    },
    canCancelAppointment(appointment) {
      if (!this.currentUser) return false;
      const currentUserId = this.currentUser.id;
      let isMainDoctor = false;
      
      if (appointment.doctor) {
        if (typeof appointment.doctor === 'object' && appointment.doctor.user) {
          isMainDoctor = appointment.doctor.user.id === currentUserId;
        } else if (typeof appointment.doctor === 'number') {
          const doctorObj = this.doctorsData[appointment.doctor];
          if (doctorObj && doctorObj.user) {
            isMainDoctor = doctorObj.user.id === currentUserId;
          }
        }
      }
      
      if (!isMainDoctor && appointment.user) {
        isMainDoctor = appointment.user.id === currentUserId;
      }
      
      return (appointment.status === 'scheduled' || appointment.status === 'in_progress') && isMainDoctor;
    },
    canCompleteAppointment(appointment) {
      if (!this.currentUser) return false;
      const currentUserId = this.currentUser.id;
      let isMainDoctor = false;
      
      if (appointment.doctor) {
        if (typeof appointment.doctor === 'object' && appointment.doctor.user) {
          isMainDoctor = appointment.doctor.user.id === currentUserId;
        } else if (typeof appointment.doctor === 'number') {
          const doctorObj = this.doctorsData[appointment.doctor];
          if (doctorObj && doctorObj.user) {
            isMainDoctor = doctorObj.user.id === currentUserId;
          }
        }
      }
      
      if (!isMainDoctor && appointment.user) {
        isMainDoctor = appointment.user.id === currentUserId;
      }
      
      return appointment.status === 'in_progress' && isMainDoctor;
    },
    canLeaveConsilium(appointment) {
      // Only consilium doctors can leave consilium
      if (!this.currentUser) return false;
      
      const currentUserId = this.currentUser.id;
      
      if (!appointment.is_consilium || !appointment.consilium_doctors) {
        return false;
      }
      
      // Check if current user is a consilium doctor (not the main doctor)
      const isConsiliumDoctor = appointment.consilium_doctors.some(doctor => 
        doctor.user && doctor.user.id === currentUserId
      );
      const isMainDoctor = appointment.doctor && appointment.doctor.user && 
                          appointment.doctor.user.id === currentUserId;
      
      return isConsiliumDoctor && !isMainDoctor && 
             (appointment.status === 'scheduled' || appointment.status === 'in_progress');
    },
    confirmLeaveConsilium(appointment) {
      if (confirm(this.$t('consilium.leaveConsiliumConfirm'))) {
        this.leaveConsilium(appointment);
      }
    },
    async leaveConsilium(appointment) {
      try {
        await api.leaveConsilium(appointment.id);
        
        // Show success notification
        this.showNotification(this.$t('consilium.leaveConsiliumSuccess'), 'success');
        
        // Refresh appointments to show updated data
        await this.fetchAppointments();
        
      } catch (error) {
        let errorMessage = this.$t('consilium.leaveConsiliumError');
        
        if (error.response && error.response.data && error.response.data.detail) {
          errorMessage = error.response.data.detail;
        }
        
        this.showNotification(errorMessage, 'error');
      }
    },
    
    async completeConsultation(appointment) {
      try {
        
        // Open the conclusion modal instead of directly completing
        this.selectedAppointmentForConclusion = appointment;
        this.resetConclusionForm();
        this.showConclusionModal = true;
      } catch (error) {
        alert('Произошла ошибка при открытии формы заключения.');
      }
    },
    
    async cancelAppointment(appointment) {
      const isNurseCall = appointment.call_type === 'nurse_call'
      const message = isNurseCall ? 
        this.$t('messages.cancelVideoCallConfirm') : 
        this.$t('messages.cancellationConfirm')
        
      if (confirm(message)) {
        try {
          await api.cancelAppointment(appointment.id)
          appointment.status = 'cancelled'
        } catch (error) {
          alert(this.$t('messages.operationFailed'))
        }
      }
    },
    
    showDetails(appointment) {
      // Показать подробную информацию о приеме или звонке
      const isNurseCall = appointment.call_type === 'nurse_call'
      const type = isNurseCall ? this.$t('nurse.videoCall') : this.$t('appointment.appointmentWithDoctor')
      const details = []
      
      details.push(`${this.$t('common.type')}: ${type}`)
      details.push(`${this.$t('common.date')}: ${this.formatDate(this.getAppointmentDate(appointment))}`)
      details.push(`${this.$t('common.time')}: ${this.formatTime(this.getAppointmentStartTime(appointment))}`)
      details.push(`${this.$t('common.status')}: ${this.getStatusText(appointment.status)}`)
      details.push(`${this.$t('appointment.provider')}: ${this.getProviderName(appointment)}`)
      
      if (appointment.reason) {
        details.push(`${this.$t('common.reason')}: ${appointment.reason}`)
      }
      
      if (isNurseCall && appointment.notes) {
        details.push(`${this.$t('appointment.notes')}: ${appointment.notes}`)
      }
      
      if (isNurseCall && appointment.duration_minutes) {
        details.push(`${this.$t('time.duration')}: ${appointment.duration_minutes} ${this.$t('time.minute')}`)
      }
      
      alert(`${this.$t('appointment.appointmentDetails')}:\n\n${details.join('\n')}`)
    },
    
    async showRoomRecords(appointment) {
      try {
        if (!appointment || !this.hasAppointmentMedia(appointment)) {
          this.roomRecordsError = 'Для этого приема нет доступных медиа-данных.';
          this.showRoomRecordsDialog = true;
          this.selectedAppointmentForRecords = appointment;
          this.resetMediaAccessNotice();
          return;
        }

        // Устанавливаем состояние диалога
        this.showRoomRecordsDialog = true;
        this.selectedAppointmentForRecords = appointment;
        this.roomRecordsLoading = true;
        this.roomRecords = [];
        this.roomRecordsError = null;
        this.resetMediaAccessNotice();

        const accessResponse = await staffApi.getAppointmentMediaAccess(appointment.id);
        const mediaAccess = accessResponse.data || {};

        if (!mediaAccess.can_view) {
          this.setMediaAccessNotice(mediaAccess);
          return;
        }

        const response = await staffApi.getAppointmentMediaRecords(appointment.id);
        
        if (response.data && response.data.result === 'success' && response.data.data) {
          this.roomRecords = response.data.data.record_urls || [];
        } else if (response.data && response.data.result === 'error') {
          this.roomRecordsError = response.data.error || 'Ошибка получения записей';
        } else {
          this.roomRecordsError = 'Неожиданный формат ответа сервера';
        }
      } catch (error) {
        if (error.response && error.response.status === 403 && error.response.data && error.response.data.media_access) {
          this.setMediaAccessNotice(error.response.data.media_access, error.response.data.detail);
        } else {
          this.roomRecordsError = 'Не удалось загрузить записи встречи. Возможно, записи еще не готовы.'
        }
      } finally {
        this.roomRecordsLoading = false;
      }
    },
    
    closeRoomRecordsDialog() {
      this.showRoomRecordsDialog = false;
      this.selectedAppointmentForRecords = null;
      this.roomRecords = [];
      this.roomRecordsError = null;
      this.resetMediaAccessNotice();
      this.closeVideoPlayer(); // Закрываем плеер при закрытии диалога
      this.closeChatPanel(); // Закрываем чат при закрытии диалога
    },
    
    playVideo(videoUrl) {
      this.selectedVideoUrl = videoUrl;
      this.showVideoPlayer = true;
    },
    
    closeVideoPlayer() {
      this.selectedVideoUrl = null;
      this.showVideoPlayer = false;
    },
    
    onVideoLoaded() {
      // No-op hook for future video metadata handling
    },
    
    // Chat methods
    async loadRoomChat(appointmentId) {
      this.roomChatLoading = true
      this.roomChatError = null
      this.roomChatMessages = []
      
      try {
        const response = await staffApi.getAppointmentMediaChat(appointmentId)
        
        if (response.data && response.data.messages) {
          this.roomChatMessages = response.data.messages || []
        } else if (response.data && response.data.result === 'error') {
          this.roomChatError = response.data.error || this.$t('patientDetails.chat.loadError')
        } else {
          this.roomChatError = this.$t('messages.chatNotEnabled')
        }
      } catch (error) {
        if (error.response && error.response.status === 403 && error.response.data && error.response.data.media_access) {
          this.closeChatPanel()
          this.setMediaAccessNotice(error.response.data.media_access, error.response.data.detail)
        } else {
          this.roomChatError = 'Не удалось загрузить сообщения чата'
        }
      } finally {
        this.roomChatLoading = false
      }
    },
    
    showChatForRoom() {
      if (!this.selectedAppointmentForRecords) return

      if (this.mediaAccessNotice.visible) {
        return
      }

      if (!this.selectedAppointmentForRecords.id) {
        this.roomChatError = 'Некорректный прием'
        return
      }
      
      if (this.showChatPanel) {
        // Если чат уже открыт, закрываем его
        this.closeChatPanel()
      } else {
        // Если чат закрыт, открываем и загружаем сообщения
        this.showChatPanel = true
        this.loadRoomChat(this.selectedAppointmentForRecords.id)
      }
    },
    
    closeChatPanel() {
      this.showChatPanel = false
      this.roomChatMessages = []
      this.roomChatError = null
    },
    resetMediaAccessNotice() {
      this.mediaAccessNotice = {
        visible: false,
        status: null,
        title: '',
        description: '',
        canRequest: false,
        requestSending: false
      }
    },
    setMediaAccessNotice(mediaAccess, detail = '') {
      const status = mediaAccess?.status || 'not_requested'
      let title = 'Нужно разрешение пациента'
      let description = 'В этом приеме есть видеозапись и чат. Для просмотра отправьте запрос пациенту.'
      let canRequest = Boolean(mediaAccess?.can_request_permission)

      if (status === 'no_media') {
        title = 'Запись недоступна'
        description = 'Для этого приема пока нет видеозаписи и истории чата.'
        canRequest = false
      } else if (status === 'pending') {
        title = 'Запрос уже отправлен'
        description = 'Пациент еще не принял решение. Как только доступ будет одобрен, вы сможете открыть видео и чат.'
        canRequest = false
      } else if (status === 'rejected') {
        title = 'Запрос был отклонен'
        description = 'Пациент отклонил предыдущий запрос. При необходимости можно отправить запрос повторно.'
        canRequest = Boolean(mediaAccess?.can_request_permission)
      } else if (status === 'forbidden' && detail) {
        title = 'Доступ ограничен'
        description = detail
        canRequest = false
      }

      this.mediaAccessNotice = {
        visible: true,
        status,
        title,
        description,
        canRequest,
        requestSending: false
      }
    },
    async requestMediaAccessForSelectedAppointment() {
      if (!this.selectedAppointmentForRecords || !this.selectedAppointmentForRecords.id) return

      this.mediaAccessNotice = {
        ...this.mediaAccessNotice,
        requestSending: true
      }

      try {
        const response = await staffApi.requestAppointmentMediaAccess(this.selectedAppointmentForRecords.id)
        const mediaAccess = response.data?.media_access || { status: 'pending', can_request_permission: false }
        this.setMediaAccessNotice(mediaAccess)
      } catch (error) {
        this.mediaAccessNotice = {
          ...this.mediaAccessNotice,
          requestSending: false,
          description: 'Не удалось отправить запрос. Проверьте соединение и попробуйте еще раз.'
        }
      }
    },
    
    formatChatTimestamp(timestamp) {
      if (!timestamp) return ''
      
      // Обрабатываем и ISO строку (created_at) и unix timestamp (timestamp)
      let date
      if (typeof timestamp === 'string') {
        date = new Date(timestamp)
      } else if (typeof timestamp === 'number') {
        // Unix timestamp в секундах, конвертируем в миллисекунды
        date = new Date(timestamp * 1000)
      } else {
        return ''
      }
      
      return date.toLocaleTimeString('ru-RU', { 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit'
      })
    },
    hasAppointmentMedia(appointment) {
      return Boolean(appointment && (appointment.has_media_content || appointment.video_room_name))
    },
    
    getMessageText(message) {
      try {
        // Если есть прямое поле text (из mock данных)
        if (message.text) {
          return message.text;
        }
        
        // Парсим JSON из поля message
        if (message.message) {
          const parsedMessage = JSON.parse(message.message);
          return parsedMessage.text || null;
        }
      } catch (error) {
        // Failed to parse message JSON
        // Возвращаем сырое сообщение если парсинг не удался
        return message.message || null;
      }
      return null;
    },
    
    getMessageFiles(message) {
      try {
        // Если есть прямое поле files (из mock данных)
        if (message.files) {
          return message.files;
        }
        
        // Парсим JSON из поля message
        if (message.message) {
          const parsedMessage = JSON.parse(message.message);
          return parsedMessage.files || [];
        }
      } catch (error) {
        // Failed to parse message JSON for files
        return [];
      }
      return [];
    },
    
    openImageModal(url, alt) {
      this.modalImageUrl = url
      this.modalImageAlt = alt
      this.showImageModal = true
    },
    
    closeImageModal() {
      this.showImageModal = false
      this.modalImageUrl = null
      this.modalImageAlt = null
    },
    
    formatFileSize(size) {
      if (size) {
        if (size < 1024) {
          return size + ' байт';
        } else if (size < 1024 * 1024) {
          return (size / 1024).toFixed(2) + ' КБ';
        } else {
          return (size / (1024 * 1024)).toFixed(2) + ' МБ';
        }
      }
      return 'Неизвестный размер';
    },
    
    // Appointment conclusion methods
    resetConclusionForm() {
      this.conclusionForm = {
        complaints: '',
        disease_history: '',
        d_registration: '',
        d_registration_details: '',
        tbs: '',
        tbs_details: '',
        allergies: '',
        allergies_details: '',
        viral_hepatitis: '',
        viral_hepatitis_details: '',
        temperature: '',
        blood_pressure: '',
        pulse: '',
        respiratory_rate: '',
        edema: '',
        edema_details: '',
        skin_condition: '',
        skin_condition_details: '',
        urination: '',
        stool: '',
        genital_discharge: '',
        genital_discharge_details: '',
        examinations: '',
        recommendations: '',
        final_protocol: ''
      };
    },
    
    loadConclusionToForm(conclusionData) {
      if (!conclusionData) return;
      
      // Загружаем данные заключения в форму
      this.conclusionForm = {
        complaints: conclusionData.complaints || '',
        disease_history: conclusionData.disease_history || '',
        d_registration: conclusionData.d_registration || '',
        d_registration_details: conclusionData.d_registration_details || '',
        tbs: conclusionData.tbs || '',
        tbs_details: conclusionData.tbs_details || '',
        allergies: conclusionData.allergies || '',
        allergies_details: conclusionData.allergies_details || '',
        viral_hepatitis: conclusionData.viral_hepatitis || '',
        viral_hepatitis_details: conclusionData.viral_hepatitis_details || '',
        temperature: conclusionData.temperature || '',
        blood_pressure: conclusionData.blood_pressure || '',
        pulse: conclusionData.pulse || '',
        respiratory_rate: conclusionData.respiratory_rate || '',
        edema: conclusionData.edema || '',
        edema_details: conclusionData.edema_details || '',
        skin_condition: conclusionData.skin_condition || '',
        skin_condition_details: conclusionData.skin_condition_details || '',
        urination: conclusionData.urination || '',
        stool: conclusionData.stool || '',
        genital_discharge: conclusionData.genital_discharge || '',
        genital_discharge_details: conclusionData.genital_discharge_details || '',
        examinations: conclusionData.examinations || '',
        recommendations: conclusionData.recommendations || '',
        final_protocol: conclusionData.final_protocol || ''
      };
      
    },
    
    async showAppointmentConclusion(appointment) {
      if (!appointment || appointment.status !== 'completed') {
        alert('Заключение доступно только для завершенных приемов');
        return;
      }
      
      try {
        this.conclusionLoading = true;
        
        
        // Устанавливаем прием для просмотра
        this.selectedAppointmentForConclusion = appointment;
        this.resetConclusionForm();
        
        // Сначала проверяем локально сохраненные данные
        if (appointment.conclusion_data) {
          this.loadConclusionToForm(appointment.conclusion_data);
        } else {
          // Принудительная загрузка с сервера с добавлением timestamp для обхода кэша
          const timestamp = new Date().getTime();
          const response = await api.getAppointmentConclusion(appointment.id, { t: timestamp });
        
          if (response.data) {
            this.loadConclusionToForm(response.data);
            
            // Кэшируем данные локально для последующих просмотров
            const appointmentIndex = this.appointments.findIndex(a => a.id === appointment.id);
            if (appointmentIndex !== -1) {
              this.appointments[appointmentIndex].conclusion_data = response.data;
            }
          } else {
      // No action needed
          }
        }
        
        // Открываем модальное окно просмотра
        this.showConclusionModal = true;
        
      } catch (error) {
        
        // Обработка различных типов ошибок
        if (error.response && error.response.status === 404) {
          alert('Заключение для данного приема еще не создано');
        } else if (error.message === 'Заключение еще не создано для данного приема') {
          alert('Заключение для данного приема еще не создано');
        } else {
          alert(this.$t('messages.conclusionLoadError'));
        }
      } finally {
        this.conclusionLoading = false;
      }
    },
    
    closeConclusionModal() {
      this.showConclusionModal = false;
      this.selectedAppointmentForConclusion = null;
      this.resetConclusionForm();
    },
    
    updateFinalProtocol() {
      // Генерируем финальный протокол на основе заполненных полей
      let protocol = '';
      
      if (this.conclusionForm.complaints) {
        protocol += `Жалобы: ${this.conclusionForm.complaints}\n\n`;
      }
      
      if (this.conclusionForm.disease_history) {
        protocol += `Анамнез заболевания: ${this.conclusionForm.disease_history}\n\n`;
      }
      
      // Анамнез жизни
      let lifeHistory = [];
      if (this.conclusionForm.d_registration === 'true') {
        lifeHistory.push(`Состоит на "Д" учете${this.conclusionForm.d_registration_details ? ': ' + this.conclusionForm.d_registration_details : ''}`);
      }
      if (this.conclusionForm.tbs === 'true') {
        lifeHistory.push(`TBS${this.conclusionForm.tbs_details ? ': ' + this.conclusionForm.tbs_details : ''}`);
      }
      if (this.conclusionForm.allergies === 'true') {
        lifeHistory.push(`Аллергоанамнез${this.conclusionForm.allergies_details ? ': ' + this.conclusionForm.allergies_details : ''}`);
      }
      if (this.conclusionForm.viral_hepatitis === 'true') {
        lifeHistory.push(`Вирусные гепатиты${this.conclusionForm.viral_hepatitis_details ? ': ' + this.conclusionForm.viral_hepatitis_details : ''}`);
      }
      
      if (lifeHistory.length > 0) {
        protocol += `Анамнез жизни: ${lifeHistory.join(', ')}\n\n`;
      }
      
      // Физикальные данные
      let physicalData = [];
      if (this.conclusionForm.temperature) physicalData.push(`температура: ${this.conclusionForm.temperature}`);
      if (this.conclusionForm.blood_pressure) physicalData.push(`АД: ${this.conclusionForm.blood_pressure}`);
      if (this.conclusionForm.pulse) physicalData.push(`пульс: ${this.conclusionForm.pulse}`);
      if (this.conclusionForm.respiratory_rate) physicalData.push(`ЧД: ${this.conclusionForm.respiratory_rate}`);
      
      if (physicalData.length > 0) {
        protocol += `Объективные данные: ${physicalData.join(', ')}\n\n`;
      }
      
      if (this.conclusionForm.examinations) {
        protocol += `Назначенные исследования: ${this.conclusionForm.examinations}\n\n`;
      }
      
      if (this.conclusionForm.recommendations) {
        protocol += `Рекомендации: ${this.conclusionForm.recommendations}\n\n`;
      }
      
      this.conclusionForm.final_protocol = protocol.trim();
    },

    copyProtocol() {
      if (this.conclusionForm.final_protocol) {
        navigator.clipboard.writeText(this.conclusionForm.final_protocol).then(() => {
          alert('Протокол скопирован в буфер обмена');
        }).catch(() => {
          alert('Не удалось скопировать протокол');
        });
      }
    },

    async saveAppointmentConclusion() {
      try {
        this.conclusionLoading = true;
        
        
        // Генерируем финальный протокол перед сохранением
        this.updateFinalProtocol();
        
        const conclusionData = { ...this.conclusionForm };
        
        await api.saveAppointmentConclusion(this.selectedAppointmentForConclusion.id, conclusionData);
        
        // НЕ обновляем статус вручную - он устанавливается автоматически на бэкенде
        // на основе наличия видеокомнаты и видеозаписей
        
        // Закрываем модальное окно
        this.closeConclusionModal();
        
        // Обновляем список приемов для получения актуального статуса с сервера
        await this.fetchAppointments();
        
        alert('Заключение сохранено и прием завершен');
        
      } catch (error) {
        alert('Ошибка сохранения заключения');
      } finally {
        this.conclusionLoading = false;
      }
    },

    getVisitReasonText(reasonOrAppointment) {
      if (!reasonOrAppointment) return ''

      if (typeof reasonOrAppointment === 'object') {
        const appointment = reasonOrAppointment

        if (appointment.reason_display !== null && appointment.reason_display !== undefined) {
          const reasonDisplay = String(appointment.reason_display).trim()
          if (reasonDisplay && Number.isNaN(Number(reasonDisplay))) {
            return reasonDisplay
          }
          if (reasonDisplay) {
            return this.getVisitReasonText(reasonDisplay)
          }
        }

        if (appointment.reason && typeof appointment.reason === 'object') {
          return this.getLocalizedVisitReasonName(appointment.reason)
        }

        return this.getVisitReasonText(appointment.reason)
      }

      // Если это уже текст, возвращаем как есть
      if (typeof reasonOrAppointment === 'string' && Number.isNaN(Number(reasonOrAppointment))) {
        return reasonOrAppointment
      }

      const reason = this.visitReasonsData[reasonOrAppointment]
      if (reason) {
        return this.getLocalizedVisitReasonName(reason)
      }

      return ''
    },
    
    getLocalizedVisitReasonName(reason) {
      if (!reason) return '';
      
      // Получаем текущий язык интерфейса
      const currentLocale = this.$i18n.locale;
      
      // Если текущий язык казахский и есть казахское название, используем его
      if (currentLocale === 'kk' && reason.name_kk) {
        return reason.name_kk;
      }
      
      // Во всех остальных случаях используем русское название
      return reason.name || reason.name_ru || 'Неизвестная причина';
    },
    
    async fetchVisitReasons() {
      try {
        const response = await api.getVisitReasons()
        const reasons = Array.isArray(response.data)
          ? response.data
          : (Array.isArray(response.data?.results) ? response.data.results : [])

        if (reasons.length > 0) {
          // Создаем объект для быстрого поиска по ID
          this.visitReasonsData = {}
          reasons.forEach(reason => {
            this.visitReasonsData[reason.id] = reason
          })
        }
      } catch (error) {
      // Error handled silently
      }
    },
    
    // Date range filter methods
    onDateRangeChange() {
      // Фильтрация теперь происходит автоматически через computed свойство currentAppointments
    },
    
    resetDateRange() {
      // Сбрасываем диапазон дат
      this.dateRange.startDate = '';
      this.dateRange.endDate = '';
    },
    
    showNotification(message, type = 'info') {
      // Создаем красивое toast-уведомление
      const toast = {
        id: Date.now() + Math.random(),
        title: type === 'success' ? this.$t('messages.successTitle') : (type === 'error' ? this.$t('messages.errorTitle') : this.$t('messages.infoTitle')),
        message: message,
        type: type,
        show: false
      };
      
      this.toasts.push(toast);
      
      // Показываем уведомление с небольшой задержкой для анимации
      setTimeout(() => {
        toast.show = true;
      }, 100);
      
      // Автоматически убираем уведомление через 5 секунд
      setTimeout(() => {
        this.removeToast(toast.id);
      }, 5000);
      
      // Логирование не требуется
    },
    
    removeToast(id) {
      const toastIndex = this.toasts.findIndex(toast => toast.id === id);
      if (toastIndex > -1) {
        // Сначала скрываем с анимацией
        this.toasts[toastIndex].show = false;
        
        // Затем удаляем из массива
        setTimeout(() => {
          this.toasts = this.toasts.filter(toast => toast.id !== id);
        }, 300);
      }
    }
  }
}
</script>

<style scoped>
/* Simple, clean styles */
.transition-colors {
  transition: background-color 0.2s ease, color 0.2s ease;
}

.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Date range filter styles */
.modern-date-input {
  min-width: 140px;
}

.modern-date-input::-webkit-calendar-picker-indicator {
  color: #3b82f6;
  cursor: pointer;
}

.modern-date-input::-webkit-calendar-picker-indicator:hover {
  color: #1d4ed8;
}
</style> 
